/*
 * Magno Chocolates - City selector + delivery calendar
 * ---------------------------------------------------
 * One file for two jobs:
 *   1. Header city selector (every page)   -> [data-magno-city-select]
 *   2. Product delivery calendar (PDP)     -> [data-magno-delivery]
 *
 * All rules come from the "Ciudades de entrega" metaobjects, emitted as JSON
 * by snippets/delivery-config.liquid. No rules live in this file.
 *
 * Time is always computed in America/Bogota, never in the visitor's timezone.
 */
(function () {
  'use strict';

  var STORAGE_KEY = 'selectedOption';       // kept from the old build so returning visitors keep their city
  var NOTICE_KEY = 'hideNotice';
  var TZ = 'America/Bogota';
  var DAY_KEYS = { domingo: 0, lunes: 1, martes: 2, miercoles: 3, jueves: 4, viernes: 5, sabado: 6 };
  var CITY_EVENT = 'magno:city:change';

  /* ------------------------------------------------------------------ */
  /* Time helpers (Bogota)                                               */
  /* ------------------------------------------------------------------ */

  var intlSupported = (function () {
    try {
      new Intl.DateTimeFormat('en-US', { timeZone: TZ }).format(new Date());
      return true;
    } catch (e) {
      return false;
    }
  })();

  function pad(n) { return (n < 10 ? '0' : '') + n; }

  // Returns {y, m, d, h, min, s, ymd} for the current moment in Bogota.
  function bogotaNow() {
    var now = new Date();
    if (intlSupported) {
      var parts = new Intl.DateTimeFormat('en-US', {
        timeZone: TZ, hourCycle: 'h23',
        year: 'numeric', month: '2-digit', day: '2-digit',
        hour: '2-digit', minute: '2-digit', second: '2-digit'
      }).formatToParts(now);
      var o = {};
      for (var i = 0; i < parts.length; i++) o[parts[i].type] = parts[i].value;
      var h = parseInt(o.hour, 10);
      if (h === 24) h = 0;
      return {
        y: parseInt(o.year, 10), m: parseInt(o.month, 10), d: parseInt(o.day, 10),
        h: h, min: parseInt(o.minute, 10), s: parseInt(o.second, 10),
        ymd: o.year + '-' + o.month + '-' + o.day
      };
    }
    // Fallback: fixed UTC-5 (Colombia has no DST)
    var utc = now.getTime() + now.getTimezoneOffset() * 60000;
    var col = new Date(utc - 5 * 3600000);
    return {
      y: col.getFullYear(), m: col.getMonth() + 1, d: col.getDate(),
      h: col.getHours(), min: col.getMinutes(), s: col.getSeconds(),
      ymd: col.getFullYear() + '-' + pad(col.getMonth() + 1) + '-' + pad(col.getDate())
    };
  }

  // Calendar-day helpers. We work with local Date objects at midnight and
  // read them back with local getters, so the visitor's timezone never shifts a day.
  function ymd(date) { return date.getFullYear() + '-' + pad(date.getMonth() + 1) + '-' + pad(date.getDate()); }
  function fromYmd(s) { var p = s.split('-'); return new Date(+p[0], +p[1] - 1, +p[2]); }
  function addDays(date, n) { var d = new Date(date.getTime()); d.setDate(d.getDate() + n); return d; }
  function parseCutoff(str) {
    if (!str || !/^\d{1,2}:\d{2}$/.test(str)) return null;
    var p = str.split(':');
    return parseInt(p[0], 10) * 60 + parseInt(p[1], 10);
  }

  function safeGet(key) { try { return window.localStorage.getItem(key); } catch (e) { return null; } }
  function safeSet(key, val) { try { window.localStorage.setItem(key, val); } catch (e) { /* private mode */ } }

  function fmt(template, vars) {
    return String(template || '').replace(/\{(\w+)\}/g, function (_, k) {
      return vars[k] != null ? vars[k] : '';
    });
  }

  /* ------------------------------------------------------------------ */
  /* 1. City selector (header)                                           */
  /* ------------------------------------------------------------------ */

  function initSelector() {
    var all = function () { return Array.prototype.slice.call(document.querySelectorAll('[data-magno-city-select]')); };
    var selects = all();
    if (!selects.length) return;

    var options = Array.prototype.slice.call(selects[0].options);

    function hasOption(value) {
      return options.some(function (o) { return o.value === value; });
    }

    // The announcement bar may clone the dropdown (ticker/slider), so every copy is kept in sync.
    function setAll(value) {
      all().forEach(function (s) { if (s.value !== value) s.value = value; });
    }

    function notices() { return Array.prototype.slice.call(document.querySelectorAll('[data-magno-city-notice]')); }

    function hideNotice() {
      notices().forEach(function (n) { n.classList.add('hidden'); });
      safeSet(NOTICE_KEY, 'true');
    }

    function announce(handle) {
      document.dispatchEvent(new CustomEvent(CITY_EVENT, { detail: { handle: handle }, bubbles: true }));
      // Legacy event name, kept in case any app or snippet still listens.
      document.dispatchEvent(new CustomEvent('regionChanged', { detail: { selectedOption: handle } }));
    }

    // "USA" is not a delivery city: open the US site in a new tab and put the dropdown back on a real city.
    function redirectIfUsa(select) {
      var opt = select.options[select.selectedIndex];
      var url = opt && opt.getAttribute('data-redirect');
      if (!url) return false;
      window.open(url, '_blank', 'noopener');
      var back = safeGet(STORAGE_KEY);
      if (!back || back === 'usa' || !hasOption(back)) back = hasOption('medellin') ? 'medellin' : options[0].value;
      setAll(back);
      safeSet(STORAGE_KEY, back);
      return true;
    }

    // 1) URL param wins (?region=cali)
    var region = new URLSearchParams(window.location.search).get('region');
    if (region && hasOption(region)) {
      safeSet(STORAGE_KEY, region);
      setAll(region);
    } else {
      // 2) stored choice
      var stored = safeGet(STORAGE_KEY);
      if (stored && hasOption(stored) && stored !== 'usa') {
        setAll(stored);
      } else if (stored === 'usa') {
        notices().forEach(function (n) { n.classList.remove('hidden'); });
      }
    }

    // Notice bubble ("enter your city")
    if (safeGet(NOTICE_KEY)) hideNotice();
    else notices().forEach(function (n) { n.classList.remove('hidden'); });

    // Delegated, so clones created later by the theme's slider also work.
    document.addEventListener('change', function (e) {
      var select = e.target && e.target.closest && e.target.closest('[data-magno-city-select]');
      if (!select) return;
      hideNotice();
      if (redirectIfUsa(select)) return;
      var handle = select.value;
      safeSet(STORAGE_KEY, handle);
      setAll(handle);
      announce(handle);
    });
    document.addEventListener('click', function (e) {
      if (e.target && e.target.closest && e.target.closest('[data-magno-city-select]')) hideNotice();
    });

    // 3) First visit only: guess from IP, after the page is idle. Never blocks render.
    if (!safeGet(STORAGE_KEY) && !/(^|;\s*)visited=/.test(document.cookie)) {
      var run = function () {
        document.cookie = 'visited=true; max-age=' + (30 * 86400) + '; path=/';
        fetch('https://ipapi.co/json/')
          .then(function (r) { return r.json(); })
          .then(function (data) {
            if (data.country_code !== 'CO') return;
            var guess = normalize(data.city || '');
            var match = options.filter(function (o) {
              var m = o.getAttribute('data-match');
              return m && normalize(m) === guess;
            })[0];
            var handle = match ? match.value : (hasOption('otra') ? 'otra' : null);
            if (handle && handle !== selects[0].value) {
              setAll(handle);
              safeSet(STORAGE_KEY, handle);
              announce(handle);
            }
          })
          .catch(function () { /* ignore */ });
      };
      if ('requestIdleCallback' in window) requestIdleCallback(run, { timeout: 3000 });
      else setTimeout(run, 1500);
    }
  }

  // Accent-insensitive compare for city names / weekday keys (no escape sequences on purpose:
  // the theme upload path does not reliably preserve backslashes).
  function normalize(s) {
    return String(s).toLowerCase()
      .replace(/[áàäâ]/g, 'a').replace(/[éèëê]/g, 'e').replace(/[íìïî]/g, 'i')
      .replace(/[óòöô]/g, 'o').replace(/[úùüû]/g, 'u').replace(/ñ/g, 'n').trim();
  }

  /* ------------------------------------------------------------------ */
  /* 2. Delivery calendar (product page)                                 */
  /* ------------------------------------------------------------------ */

  function readConfig(root, scope) {
    var el = null;
    if (root) el = root.querySelector('[data-magno-delivery-config]');
    if (!el && scope) el = document.querySelector('[data-magno-delivery-config="' + scope + '"]');
    if (!el) el = document.querySelector('[data-magno-delivery-config]');
    if (!el) return null;
    try { return JSON.parse(el.textContent); } catch (e) { console.error('[Magno] delivery config JSON is invalid', e); return null; }
  }

  function normalizeCity(c) {
    return {
      handle: c.handle,
      nameEs: c.nameEs || c.handle,
      nameEn: c.nameEn || c.nameEs || c.handle,
      order: typeof c.order === 'number' ? c.order : 99,
      isGlobal: !!c.isGlobal,
      active: !!c.active,
      sameDay: !!c.sameDay,
      cutoff: parseCutoff(c.cutoff),
      cutoffByDay: parseCutoffByDay(c.cutoffByDay),
      days: Array.isArray(c.days) ? c.days.map(function (d) { return DAY_KEYS[normalize(d)]; }).filter(function (d) { return d != null; }) : [],
      closed: Array.isArray(c.closed) ? c.closed : [],
      open: Array.isArray(c.open) ? c.open : [],
      transitMin: c.transitMin || 0,
      transitMax: c.transitMax || 0,
      messageEs: c.messageEs || '',
      messageEn: c.messageEn || '',
      pickupEs: c.pickupEs || '',
      pickupEn: c.pickupEn || '',
      location: c.location || ''
    };
  }

  // ["sábado 12:00", "domingo 11:30"] -> { 6: 720, 0: 690 }
  function parseCutoffByDay(list) {
    var out = {};
    if (!Array.isArray(list)) return out;
    list.forEach(function (item) {
      var m = /^\s*(\S+)\s+(\d{1,2}:\d{2})\s*$/.exec(String(item || ''));
      if (!m) return;
      var dow = DAY_KEYS[normalize(m[1])];
      var mins = parseCutoff(m[2]);
      if (dow != null && mins != null) out[dow] = mins;
    });
    return out;
  }

  // Cutoff (minutes) that applies to a given weekday for a city: per-day exception, else the general one.
  function cutoffFor(city, dow) {
    if (!city) return null;
    if (city.cutoffByDay && city.cutoffByDay[dow] != null) return city.cutoffByDay[dow];
    return city.cutoff;
  }

  /* ------------------------------------------------------------------ */
  /* Shared rules + formatting (product calendar and header announcement) */
  /* ------------------------------------------------------------------ */

  function escapeHtml(s) {
    return String(s).replace(/[&<>"]/g, function (ch) { return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[ch]; });
  }

  function buildRules(config) {
    var locale = config.locale === 'en' ? 'en' : 'es';
    var S = config.strings || {};
    var cities = (config.cities || []).map(normalizeCity);
    var byHandle = {};
    cities.forEach(function (c) { byHandle[c.handle] = c; });
    var global = cities.filter(function (c) { return c.isGlobal; })[0] || null;
    var selectable = cities.filter(function (c) { return !c.isGlobal && c.active; })
      .sort(function (a, b) { return a.order - b.order; });

    function cityName(c) { return locale === 'en' ? c.nameEn : c.nameEs; }
    function cityMessage(c) { return locale === 'en' ? c.messageEn : c.messageEs; }
    function cityPickup(c) { return locale === 'en' ? c.pickupEn : c.pickupEs; }

    function fmtClock(minutes) {
      var h = Math.floor(minutes / 60), m = minutes % 60;
      if (locale === 'en') return ((h % 12) || 12) + ':' + pad(m) + (h < 12 ? ' AM' : ' PM');
      var h12 = (h % 12) || 12;
      return (h12 === 1 ? 'la ' : 'las ') + h12 + ':' + pad(m) + (h < 12 ? ' a.m.' : ' p.m.');
    }

    // Is this calendar day open for the city (weekday, closures, exceptions)? Ignores today/cutoff logic.
    function dayOpen(r, s) {
      if (!r) return false;
      if (r.open.indexOf(s) !== -1) return true;                 // city exception beats every closure below
      if (r.closed.indexOf(s) !== -1) return false;
      if (global && global.closed.indexOf(s) !== -1) return false;
      if (r.days.length && r.days.indexOf(fromYmd(s).getDay()) === -1) return false;
      return true;
    }

    var dateFmt = new Intl.DateTimeFormat(locale === 'en' ? 'en-US' : 'es-CO', { weekday: 'short', day: 'numeric', month: 'short' });
    function fmtDate(s) { return dateFmt.format(fromYmd(s)); }
    // "jueves 24 de sept" / "Thursday, Sep 24" for the announcement bar
    var dateFmtLong = new Intl.DateTimeFormat(locale === 'en' ? 'en-US' : 'es-CO', { weekday: 'long', day: 'numeric', month: 'short' });
    function fmtDateLong(s) { var t = dateFmtLong.format(fromYmd(s)); return locale === 'en' ? t : t.replace(',', '').replace('.', ''); }

    // Weekday helpers for the info panel ("lun a sab" / "mar, jue y sab")
    var dayFmt = new Intl.DateTimeFormat(locale === 'en' ? 'en-US' : 'es-CO', { weekday: 'short' });
    function dayShort(dow) { return dayFmt.format(new Date(2026, 8, 20 + dow)).replace('.', ''); } // 2026-09-20 is a Sunday
    function daysLabel(days) {
      if (!days.length || days.length === 7) return '';
      var iso = days.map(function (d) { return d === 0 ? 7 : d; }).sort(function (a, b) { return a - b; });
      var contiguous = iso.every(function (v, i) { return i === 0 || v === iso[i - 1] + 1; });
      var names = iso.map(function (v) { return dayShort(v % 7); });
      if (contiguous && names.length > 2) return fmt(S.dayRange, { from: names[0], to: names[names.length - 1] });
      if (names.length === 1) return names[0];
      return listNames(names);
    }

    function listNames(names) {
      if (names.length <= 1) return names.join('');
      return names.slice(0, -1).join(', ') + ' ' + S.andWord + ' ' + names[names.length - 1];
    }

    // "How delivery works" panel. current = the visitor's city (drives the pickup block).
    function infoHtml(current) {
      var same = selectable.filter(function (c) { return c.sameDay; });
      var courier = selectable.filter(function (c) { return !c.sameDay; });
      var html = '';   // closures are deliberately NOT listed here (team decision: the calendar simply blocks them)
      if (same.length) {
        html += '<strong>' + escapeHtml(S.infoSameDayTitle) + '</strong><ul>' + same.map(function (c) {
          var d = daysLabel(c.days);
          var t = c.cutoff != null ? fmtClock(c.cutoff) : '';
          var ex = Object.keys(c.cutoffByDay || {}).map(function (dow) {
            return fmt(S.infoCutoffDay, { day: dayShort(+dow), time: fmtClock(c.cutoffByDay[dow]) });
          });
          if (ex.length) t += ' (' + ex.join(', ') + ')';
          return '<li>' + escapeHtml(fmt(d ? S.infoItemDays : S.infoItem, { city: cityName(c), time: t, days: d })) + '</li>';
        }).join('') + '</ul>';
        if (S.infoSameDayNote) html += '<p>' + escapeHtml(S.infoSameDayNote) + '</p>';
      }
      courier.forEach(function (c) {
        html += '<p><strong>' + escapeHtml(cityName(c)) + '</strong><br>' +
          escapeHtml(fmt(S.infoCourier, { days: daysLabel(c.days) || S.everyDay, min: c.transitMin, max: c.transitMax })) + '</p>';
      });
      var pickup = current ? cityPickup(current) : '';
      if (pickup) {
        html += '<p><strong>' + escapeHtml(fmt(S.infoPickupTitle, { city: cityName(current) })) + '</strong><br>' +
          escapeHtml(pickup).split(/\r?\n+/).join('<br>') + '</p>';
      }
      html += '<p>' + escapeHtml(S.infoHolidays) + '</p>';
      return html;
    }

    // Upcoming exceptions from the metaobjects (fechas_cerradas / fechas_abiertas), future dates only.
    // Kept for reuse; currently not rendered anywhere (see infoHtml).
    var longFmt = new Intl.DateTimeFormat(locale === 'en' ? 'en-US' : 'es-CO', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });
    function longDate(s) { var t = longFmt.format(fromYmd(s)); return t.charAt(0).toUpperCase() + t.slice(1); }
    // Only dates the calendar can still offer (today .. maxDays ahead); older or far-future ones are noise.
    function futureDates(list, today, limit) {
      return (list || []).filter(function (s) { return /^\d{4}-\d{2}-\d{2}$/.test(s) && s >= today && s <= limit; }).sort();
    }
    function closuresHtml() {
      var today = bogotaNow().ymd;
      var limit = ymd(addDays(fromYmd(today), config.maxDays || 60));
      var html = '';
      function line(title, dates) {
        if (!dates.length) return;
        html += '<p class="magno-info__closed"><strong>' + escapeHtml(title) + ' ' + dates.map(function (d) { return escapeHtml(longDate(d)); }).join(' • ') + '</strong></p>';
      }
      if (global) line(S.closedAll, futureDates(global.closed, today, limit));
      selectable.forEach(function (c) {
        line(fmt(S.closedCity, { city: cityName(c) }), futureDates(c.closed, today, limit));
        line(fmt(S.openCity, { city: cityName(c) }), futureDates(c.open, today, limit));
      });
      return html;
    }

    function faqLinkHtml() {
      return (S.faqUrl && S.faqLink) ? '<p class="magno-info__faq"><a href="' + escapeHtml(S.faqUrl) + '" target="_blank" rel="noopener">' + escapeHtml(S.faqLink) + '</a></p>' : '';
    }

    // Product-page (i): only what changes with the visitor's city (store pickup), plus a link to the FAQ.
    function pickupHtml(current) {
      var html = '';
      // Same-day cities: how the route works (courier cities get the transit line in the card instead).
      if (current && current.sameDay && S.infoDeliveryNote) html += '<p>' + escapeHtml(S.infoDeliveryNote) + '</p>';
      var pickup = current ? cityPickup(current) : '';
      if (pickup) {
        html += '<strong>' + escapeHtml(fmt(S.infoPickupTitle, { city: cityName(current) })) + '</strong><p>' +
          escapeHtml(pickup).split(/\r?\n+/).join('<br>') + '</p>';
      } else if (current && S.infoNoPickup) {
        html += '<p>' + escapeHtml(fmt(S.infoNoPickup, { city: cityName(current) })) + '</p>';
      }
      return html + faqLinkHtml();
    }

    function storedCity() {
      var stored = safeGet(STORAGE_KEY);
      var c = stored && byHandle[stored];
      return (c && !c.isGlobal && c.active) ? c : selectable[0] || null;
    }

    return {
      locale: locale, S: S, cities: cities, byHandle: byHandle, global: global, selectable: selectable,
      cityName: cityName, cityMessage: cityMessage, cityPickup: cityPickup,
      fmtClock: fmtClock, fmtDate: fmtDate, fmtDateLong: fmtDateLong, dateFmt: dateFmt, dayShort: dayShort, daysLabel: daysLabel, listNames: listNames,
      dayOpen: dayOpen, infoHtml: infoHtml, pickupHtml: pickupHtml, faqLinkHtml: faqLinkHtml, storedCity: storedCity
    };
  }

  /* ------------------------------------------------------------------ */
  /* 1b. Header announcement: live delivery line, links to the FAQ       */
  /* ------------------------------------------------------------------ */

  function initAnnouncement() {
    // Product pages carry their own promise card; the ticker text is hidden there (CSS on body.template-product).
    if (document.body && document.body.classList.contains('template-product')) return;
    var spots = function () { return Array.prototype.slice.call(document.querySelectorAll('.theme__header__toolbar__text [data-ticker-text]')); };
    if (!spots().length) return;
    var config = readConfig(null, 'global');
    if (!config) return;
    var R = buildRules(config);
    var S = R.S;
    var ICON = '<svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M3 7h11v9H3zM14 10h4l3 3v3h-7z"/><circle cx="7" cy="17.5" r="1.8"/><circle cx="17" cy="17.5" r="1.8"/></svg>';
    var href = S.faqUrl || '/pages/preguntas-frecuentes#entregas';

    // For the visitor's city: still open for today (with its cutoff), else the next available date.
    function text() {
      var now = bogotaNow();
      var today = now.ymd;
      var tomorrow = ymd(addDays(fromYmd(today), 1));
      var dow = fromYmd(today).getDay();
      var nowMin = now.h * 60 + now.min;
      var c = R.storedCity();
      if (!c) return null;
      var name = R.cityName(c);
      var cut = cutoffFor(c, dow);
      if (c.sameDay && R.dayOpen(c, today) && cut != null && nowMin < cut) {
        return fmt(S.announceCityToday, { city: name, time: R.fmtClock(cut) });
      }
      var first = null;
      for (var i = 1; i <= (config.maxDays || 60); i++) {
        var s = ymd(addDays(fromYmd(today), i));
        if (R.dayOpen(c, s)) { first = s; break; }
      }
      if (!first) return null;
      if (first === tomorrow) return fmt(c.sameDay ? S.announceFromTomorrow : S.announceShipFromTomorrow, { city: name });
      return fmt(c.sameDay ? S.announceFromDate : S.announceShipFromDate, { city: name, date: R.fmtDateLong(first) });
    }

    var last = '';
    function render(force) {
      var t = text();
      if (t === null) return;
      if (t === last && !force) return;
      last = t;
      // The whole line links to the FAQ delivery section. The theme's ticker clones the text node
      // to measure/scroll; every copy gets the same content.
      spots().forEach(function (el) {
        var html = '<p><a class="magno-announce__link" href="' + escapeHtml(href) + '">' + ICON + '<span>' + escapeHtml(t) + '</span></a></p>';
        if (el.innerHTML !== html) el.innerHTML = html;
      });
    }
    render();
    setTimeout(function () { render(true); }, 1500);   // after the ticker has cloned
    setInterval(render, 60000);                          // flips at cutoff / midnight
    document.addEventListener(CITY_EVENT, function () { render(true); });
  }

  /* ------------------------------------------------------------------ */
  /* 1c. FAQ page: live "how delivery works" block for the chosen city   */
  /* ------------------------------------------------------------------ */

  function initFaq() {
    var hosts = Array.prototype.slice.call(document.querySelectorAll('[data-magno-faq-delivery]'));
    if (!hosts.length) return;
    var config = readConfig(null, 'global');
    if (!config) return;
    var R = buildRules(config);
    var S = R.S;

    function render() {
      var city = R.storedCity();
      hosts.forEach(function (host) {
        var options = R.selectable.map(function (c) {
          return '<option value="' + c.handle + '"' + (city && city.handle === c.handle ? ' selected' : '') + '>' + escapeHtml(R.cityName(c)) + '</option>';
        }).join('');
        host.innerHTML =
          '<p class="magno-faq__pick"><label>' + escapeHtml(S.faqShowing) + ' <select data-magno-faq-city aria-label="' + escapeHtml(S.changeCity) + '">' + options + '</select></label></p>' +
          '<div class="magno-faq__body">' + R.infoHtml(city) + '</div>';
      });
    }
    render();
    document.addEventListener(CITY_EVENT, render);
    // Changing the city here also changes it for the header and the product pages.
    document.addEventListener('change', function (e) {
      var sel = e.target && e.target.closest && e.target.closest('[data-magno-faq-city]');
      if (!sel) return;
      var handle = sel.value;
      safeSet(STORAGE_KEY, handle);
      Array.prototype.slice.call(document.querySelectorAll('[data-magno-city-select]')).forEach(function (h) { h.value = handle; });
      document.dispatchEvent(new CustomEvent(CITY_EVENT, { detail: { handle: handle }, bubbles: true }));
    });
  }

  function whenFlatpickrReady(cb) {
    var tries = 0;
    (function check() {
      if (window.flatpickr) return cb();
      if (++tries > 100) return console.error('[Magno] flatpickr never loaded');
      setTimeout(check, 100);
    })();
  }

  function initCalendar(root) {
    var config = readConfig(root, 'product');
    if (!config) return;

    var R = buildRules(config);
    var locale = R.locale, S = R.S, cities = R.cities, global = R.global, selectable = R.selectable;
    var cityName = R.cityName, cityMessage = R.cityMessage, fmtClock = R.fmtClock, fmtDate = R.fmtDate, dateFmt = R.dateFmt;
    var productBlocked = (config.productBlockedDates || []).filter(function (s) { return /^\d{4}-\d{2}-\d{2}$/.test(s); });
    var maxDays = config.maxDays || 60;

    // DOM
    var els = {
      form: root.closest('form'),
      formArea: root.querySelector('[data-delivery-form]'),
      status: root.querySelector('[data-delivery-status]'),
      globalMsg: root.querySelector('[data-global-message]'),
      cityMsg: root.querySelector('[data-city-message]'),
      agotado: root.querySelector('[data-agotado]'),
      dateInput: root.querySelector('[data-date-input]'),
      dateLabel: root.querySelector('[data-date-label]'),
      dateNotice: root.querySelector('[data-date-notice]'),
      arrival: root.querySelector('[data-arrival]'),
      arrivalProp: root.querySelector('[data-arrival-prop]'),
      cityProp: root.querySelector('[data-city-prop]'),
      message: root.querySelector('[data-message-input]'),
      chips: root.querySelector('[data-date-chips]'),
      dateError: root.querySelector('[data-date-error]'),
      messageError: root.querySelector('[data-message-error]'),
      count: root.querySelector('[data-message-count]')
    };
    var formScope = function () { return els.form || root.parentElement || document; };
    var statusEls = function () { return Array.prototype.slice.call(formScope().querySelectorAll('[data-delivery-status]')); };
    var ctaHint = function () { return formScope().querySelector('[data-cta-hint]'); };
    var addButton = function () { return formScope().querySelector('[data-add-to-cart]'); };
    var buttons = function () {
      var scope = els.form || root.parentElement || document;
      return Array.prototype.slice.call(scope.querySelectorAll('.product__submit__buttons button, [data-add-to-cart], .shopify-payment-button button'));
    };

    var fp = null;
    var state = { city: null, rules: null, status: 'normal', now: null, cutoff: null, pastCutoff: false, lastKey: '', variantId: '' };

    function currentCity() { return R.storedCity(); }

    // Selected variant (the theme updates the hidden id input on variant change).
    function currentVariantId() {
      var input = formScope().querySelector('input[name="id"]');
      return input ? String(input.value) : '';
    }

    // Inventory by Shopify location for the selected variant. A missing map (inventory not
    // tracked, or a location name that matches nothing) never blocks a sale.
    function inStock(locationName) {
      var map = config.availability && config.availability[state.variantId];
      if (!locationName || !map || !Object.keys(map).length) return true;
      if (!(locationName in map)) { return true; }
      return map[locationName] === true;
    }

    // Liquid only fills store_availabilities for the variant selected at page load, so the
    // other variants arrive with an empty map. Fetch theirs (once) from the
    // product.magno-availability alternate template when the visitor switches variant.
    var availabilityFetched = {};
    function availabilityKnown(variantId) {
      var map = config.availability && config.availability[variantId];
      return !!(map && Object.keys(map).length) || availabilityFetched[variantId] === true;
    }
    function fetchAvailability(variantId) {
      if (!variantId || availabilityFetched[variantId]) return;
      availabilityFetched[variantId] = 'pending';
      var handle = (els.form && els.form.getAttribute('data-product-handle')) || (String(window.location && window.location.pathname).match(/\/products\/([^\/?#]+)/) || [])[1];
      if (!handle || typeof window.fetch !== 'function') { availabilityFetched[variantId] = true; return; }
      fetch('/products/' + handle + '?view=magno-availability&variant=' + encodeURIComponent(variantId), { credentials: 'same-origin' })
        .then(function (r) { return r.ok ? r.json() : {}; })
        .then(function (data) {
          config.availability = config.availability || {};
          if (data && data[variantId]) config.availability[variantId] = data[variantId];
        })
        .catch(function () {})
        .then(function () { availabilityFetched[variantId] = true; if (state.variantId === variantId) renderAll(); });
    }

    // Shopify inventory decides: stock at the city's location -> normal flow; none -> sold out in
    // that city (no fallback to another location: a clear "agotado" beats a surprise courier shipment).
    function resolveStatus(c) {
      if (!c || !c.location) return 'normal';
      if (!availabilityKnown(state.variantId)) {
        fetchAvailability(state.variantId);
        if (!availabilityKnown(state.variantId)) return state.status; // keep the last status until the fetch lands
      }
      return inStock(c.location) ? 'normal' : 'agotado';
    }

    function computeState() {
      state.now = bogotaNow();
      state.city = currentCity();
      state.variantId = currentVariantId();
      var c = state.city;
      var status = resolveStatus(c);
      state.status = status;
      state.rules = c;
      var r = state.rules;
      state.cutoff = r ? cutoffFor(r, fromYmd(state.now.ymd).getDay()) : null;
      state.pastCutoff = state.cutoff == null ? true : (state.now.h * 60 + state.now.min) >= state.cutoff;
    }

    function isDeliverable(s) {
      var r = state.rules;
      if (!r) return false;
      var isToday = s === state.now.ymd;
      if (isToday && (!r.sameDay || state.pastCutoff)) return false;
      if (productBlocked.indexOf(s) !== -1) return false;
      return R.dayOpen(r, s);
    }

    function firstAvailable() {
      var today = fromYmd(state.now.ymd);
      for (var i = 0; i <= maxDays; i++) {
        var s = ymd(addDays(today, i));
        if (isDeliverable(s)) return s;
      }
      return null;
    }

    function addBusinessDays(date, n) {
      var d = new Date(date.getTime());
      var added = 0;
      while (added < n) {
        d = addDays(d, 1);
        var dow = d.getDay();
        if (dow === 0 || dow === 6) continue;
        if (global && global.closed.indexOf(ymd(d)) !== -1) continue;
        added++;
      }
      return d;
    }

    /* ---- rendering ---- */

    function renderMessages() {
      var gm = global ? cityMessage(global) : '';
      var cm = state.city ? cityMessage(state.city) : '';
      setText(els.globalMsg, gm);
      setText(els.cityMsg, cm);
    }

    function setText(el, text) {
      if (!el) return;
      if (text) { el.textContent = text; el.hidden = false; }
      else { el.textContent = ''; el.hidden = true; }
    }

    function renderLabels() {
      var nac = !!(state.rules && !state.rules.sameDay);
      if (els.dateLabel) els.dateLabel.textContent = nac ? S.labelDateNacional : S.labelDate;
      if (els.dateNotice) {
        els.dateNotice.textContent = nac
          ? fmt(S.noticeDateNacional, { min: state.rules.transitMin, max: state.rules.transitMax })
          : S.noticeDate;
      }
      if (els.cityProp) els.cityProp.value = state.city ? cityName(state.city) : '';
    }

    function renderArrival() {
      var nac = !!(state.rules && !state.rules.sameDay);
      var sel = fp && fp.selectedDates[0];
      if (!nac || !sel) {
        setText(els.arrival, '');
        if (els.arrivalProp) { els.arrivalProp.value = ''; els.arrivalProp.disabled = true; }
        return;
      }
      var r = state.rules;
      var from = addBusinessDays(sel, r.transitMin);
      var to = addBusinessDays(sel, r.transitMax);
      var text = (r.transitMin === r.transitMax)
        ? fmt(S.arrivalSame, { ship: dateFmt.format(sel), from: dateFmt.format(from) })
        : fmt(S.arrival, { ship: dateFmt.format(sel), from: dateFmt.format(from), to: dateFmt.format(to) });
      setText(els.arrival, text);
      if (els.arrivalProp) {
        els.arrivalProp.value = (r.transitMin === r.transitMax) ? ymd(from) : ymd(from) + ' a ' + ymd(to);
        els.arrivalProp.disabled = false;
      }
    }

    // One source of truth for the delivery promise; rendered as a card (top of form)
    // and as a single line (under the CTA, sticky bar).
    function statusModel() {
      if (state.status === 'agotado' || !state.rules) return null;
      var r = state.rules;
      var first = firstAvailable();
      var city = state.city ? cityName(state.city) : '';
      if (!first) return { kind: 'none', city: city, title: fmt(S.noDates, { city: city }) };
      var nac = !r.sameDay;
      if (nac) {
        return {
          kind: 'ship', city: city,
          title: fmt(S.promiseShip, { date: fmtDate(first), city: city }),
          sub: fmt(S.promiseShipSub, { min: r.transitMin, max: r.transitMax }),
          line: fmt(S.lineShip, { date: fmtDate(first), min: r.transitMin, max: r.transitMax })
        };
      }
      if (first === state.now.ymd && state.cutoff != null) {
        var diff = state.cutoff * 60 - (state.now.h * 3600 + state.now.min * 60 + state.now.s);
        if (diff < 0) diff = 0;
        var hh = Math.floor(diff / 3600), mm = Math.floor((diff % 3600) / 60), ss = diff % 60;
        var short = hh > 0 ? hh + 'h ' + pad(mm) + 'm' : mm + 'm ' + pad(ss) + 's';
        return {
          kind: 'today', city: city,
          title: fmt(S.promiseToday, { city: city }),
          sub: fmt(S.promiseTodaySub, { time: fmtClock(state.cutoff) }),
          timer: [pad(hh), pad(mm), pad(ss)],
          line: fmt(S.lineToday, { city: city, left: short })
        };
      }
      var tomorrow = ymd(addDays(fromYmd(state.now.ymd), 1));
      var when = (first === tomorrow ? S.tomorrowWord + ', ' : '') + fmtDate(first);
      return {
        kind: 'next', city: city,
        title: fmt(S.promiseNext, { date: when, city: city }),
        sub: (r.sameDay && state.cutoff != null && state.pastCutoff) ? fmt(S.promiseNextSub, { time: fmtClock(state.cutoff) }) : '',
        line: fmt(S.lineNext, { date: when, city: city })
      };
    }

    var ICON_TRUCK = '<svg viewBox="0 0 24 24" width="24" height="24" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M3 7h11v9H3zM14 10h4l3 3v3h-7z"/><circle cx="7" cy="17.5" r="1.8"/><circle cx="17" cy="17.5" r="1.8"/></svg>';
    var ICON_BOX = '<svg viewBox="0 0 24 24" width="24" height="24" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M12 3l8 4.5v9L12 21l-8-4.5v-9z"/><path d="M4 7.5l8 4.5 8-4.5M12 12v9"/></svg>';

    function cardHtml(m) {
      if (!m) return '';
      var row = '<div class="magno-promise__row">' +
        (m.kind === 'none' ? '' : '<div class="magno-promise__icon">' + (m.kind === 'ship' ? ICON_BOX : ICON_TRUCK) + '</div>') +
        '<strong class="magno-promise__title" data-title></strong><span class="magno-promise__sub" data-sub hidden></span>' +
        '<button type="button" class="magno-promise__i" data-info-toggle aria-expanded="false" aria-label="' + escapeHtml(S.howItWorks) + '">i</button>' +
        '<div class="magno-promise__timer" role="timer" data-timer hidden><span>00</span><i>:</i><span>00</span><i>:</i><span>00</span></div>' +
        '</div>' +
        '<div class="magno-promise__more" data-info-panel><div class="magno-promise__more-inner" data-info-inner></div></div>' +
        '<span class="magno-promise__measure" aria-hidden="true" data-measure></span>';
      return '<div class="magno-promise magno-promise--' + m.kind + '" data-kind="' + m.kind + '">' + row + '</div>';
    }

    // The city name in the title is a native <select> styled as text: one tap opens the picker.
    function citySelectHtml() {
      return '<select class="magno-promise__cityselect" data-inline-city aria-label="' + escapeHtml(S.changeCity) + '">' +
        selectable.map(function (c) {
          return '<option value="' + c.handle + '"' + (state.city && state.city.handle === c.handle ? ' selected' : '') + '>' + escapeHtml(cityName(c)) + '</option>';
        }).join('') + '</select>';
    }

    function titleHtml(m) {
      var t = escapeHtml(m.title);
      var c = escapeHtml(m.city);
      return (m.city && t.indexOf(c) !== -1) ? t.replace(c, citySelectHtml()) : t + ' ' + citySelectHtml();
    }

    // Size the select to its selected option (a <select> otherwise sizes to its widest option).
    function sizeCitySelect(card) {
      var sel = card.querySelector('[data-inline-city]');
      var m = card.querySelector('[data-measure]');
      if (!sel || !m) return;
      var opt = sel.options[sel.selectedIndex];
      m.textContent = opt ? opt.textContent : '';
      var w = m.offsetWidth;
      if (w) sel.style.width = (w + 18) + 'px';
    }

    function wireCard(card) {
      var infoPanel = card.querySelector('[data-info-panel]');
      var infoInner = card.querySelector('[data-info-inner]');
      card.addEventListener('click', function (e) {
        var t = e.target;
        if (!t || !t.closest || !t.closest('[data-info-toggle]')) return;
        var open = !infoPanel.classList.contains('is-open');
        if (open) { infoInner.innerHTML = R.pickupHtml(state.city); card.setAttribute('data-info-city', state.city ? state.city.handle : ''); }
        infoPanel.classList.toggle('is-open', open);
        var btn = card.querySelector('[data-info-toggle]');
        if (btn) btn.setAttribute('aria-expanded', open ? 'true' : 'false');
      });
      card.addEventListener('change', function (e) {
        var sel = e.target && e.target.closest && e.target.closest('[data-inline-city]');
        if (!sel) return;
        var handle = sel.value;
        safeSet(STORAGE_KEY, handle);
        Array.prototype.slice.call(document.querySelectorAll('[data-magno-city-select]')).forEach(function (h) { h.value = handle; });
        document.dispatchEvent(new CustomEvent(CITY_EVENT, { detail: { handle: handle }, bubbles: true }));
      });
    }

    function renderCard(m) {
      var host = els.status;
      if (!host) return;
      if (!m) { host.innerHTML = ''; return; }
      var card = host.querySelector('.magno-promise');
      if (!card || card.getAttribute('data-kind') !== m.kind) {
        host.innerHTML = cardHtml(m);
        card = host.querySelector('.magno-promise');
        if (!card) return;
        wireCard(card);
      }
      var t = card.querySelector('[data-title]');
      var key = m.title + '|' + m.city;
      if (t.getAttribute('data-key') !== key) {
        t.innerHTML = titleHtml(m);
        t.setAttribute('data-key', key);
        sizeCitySelect(card);
      }
      // Keep an open (i) panel in sync when the city changes (header dropdown or inline select).
      var infoPanel = card.querySelector('[data-info-panel]');
      var cityHandle = state.city ? state.city.handle : '';
      if (infoPanel && infoPanel.classList.contains('is-open') && card.getAttribute('data-info-city') !== cityHandle) {
        card.querySelector('[data-info-inner]').innerHTML = R.pickupHtml(state.city);
        card.setAttribute('data-info-city', cityHandle);
      }
      var sub = card.querySelector('[data-sub]'); sub.textContent = m.sub || ''; sub.hidden = !m.sub;
      var timer = card.querySelector('[data-timer]');
      if (m.timer) {
        timer.hidden = false;
        var spans = timer.querySelectorAll('span');
        for (var i = 0; i < 3; i++) if (spans[i].textContent !== m.timer[i]) spans[i].textContent = m.timer[i];
      } else timer.hidden = true;
    }

    function lineHtml(m) {
      if (!m) return '';
      if (m.kind === 'none') return '<span class="magno-delivery__warn">' + escapeHtml(m.title) + '</span>';
      return escapeHtml(m.line || m.title);
    }

    function statusHtml() { return lineHtml(statusModel()); }

    function renderStatus() {
      var m = statusModel();
      var line = lineHtml(m);
      renderCard(m);
      statusEls().forEach(function (el) {
        if (el === els.status) return;
        if (el.innerHTML !== line) el.innerHTML = line;
      });
    }

    /* ---- date chips: Hoy / Manana / Otra fecha (or next ship days for courier) ---- */

    function chipCandidates() {
      var r = state.rules;
      if (!r) return [];
      var today = fromYmd(state.now.ymd);
      var out = [];
      var nac = !r.sameDay;
      if (!nac) {
        var t = ymd(today), tm = ymd(addDays(today, 1));
        if (isDeliverable(t)) out.push({ ymd: t, label: S.chipToday });
        if (isDeliverable(tm)) out.push({ ymd: tm, label: S.chipTomorrow });
      }
      // fill up to two chips with the next available dates
      for (var i = nac ? 1 : 2; i <= maxDays && out.length < 2; i++) {
        var s = ymd(addDays(today, i));
        if (isDeliverable(s) && !out.some(function (c) { return c.ymd === s; })) out.push({ ymd: s, label: fmtDate(s) });
      }
      // The earliest option reads as "as soon as possible" unless it is literally today/tomorrow.
      if (out.length && out[0].label !== S.chipToday && out[0].label !== S.chipTomorrow && S.chipAsap) out[0].label = S.chipAsap;
      return out;
    }

    function renderChips() {
      if (!els.chips) return;
      var cands = chipCandidates();
      var sel = fp && fp.selectedDates[0] ? ymd(fp.selectedDates[0]) : null;
      var html = cands.map(function (c) {
        return '<button type="button" class="magno-chip' + (sel === c.ymd ? ' is-active' : '') + '" data-chip="' + c.ymd + '">' + escapeHtml(c.label) + '</button>';
      }).join('');
      var otherActive = sel && !cands.some(function (c) { return c.ymd === sel; });
      html += '<button type="button" class="magno-chip magno-chip--other' + (otherActive ? ' is-active' : '') + '" data-chip="other">' +
        escapeHtml(otherActive ? fmtDate(sel) : S.chipOther) + '</button>';
      els.chips.innerHTML = html;
      els.chips.hidden = cands.length === 0 && !fp;
    }


    /* ---- validation: never disable the CTA; explain on tap ---- */

    function missingFields() {
      var out = [];
      if (config.schedulingOptional) return out;
      if (els.dateInput && !els.dateInput.value) out.push('date');
      var mode = config.messageMode;
      if (mode !== 'opcional' && mode !== 'esconder' && els.message && !els.message.value.trim()) out.push('message');
      return out;
    }

    function setError(el, text) {
      if (!el) return;
      el.textContent = text || '';
      el.hidden = !text;
    }

    function isCourier() { return !!(state.rules && !state.rules.sameDay); }

    function showMissing(missing) {
      var nac = isCourier();
      setError(els.dateError, missing.indexOf('date') !== -1 ? (nac ? S.errDateNacional : S.errDate) : '');
      setError(els.messageError, missing.indexOf('message') !== -1 ? S.errMessage : '');
      if (missing.indexOf('date') !== -1 && els.dateInput) els.dateInput.classList.add('is-invalid');
      if (missing.indexOf('message') !== -1 && els.message) els.message.classList.add('is-invalid');
      var first = missing[0] === 'date' ? els.dateInput : els.message;
      if (!first) return;
      var det = first.closest('details');
      if (det) det.open = true;
      try { first.scrollIntoView({ behavior: 'smooth', block: 'center' }); } catch (e) { first.scrollIntoView(); }
      setTimeout(function () {
        if (missing[0] === 'date' && fp) fp.open(); else first.focus();
      }, 350);
    }

    function guardSubmit(e) {
      if (state.status === 'agotado') { e.preventDefault(); e.stopImmediatePropagation(); return; }
      var missing = missingFields();
      if (!missing.length) return;
      e.preventDefault();
      e.stopImmediatePropagation();
      showMissing(missing);
    }

    function renderAgotado() {
      var off = state.status === 'agotado';
      if (els.agotado) els.agotado.hidden = !off;
      if (els.formArea) els.formArea.hidden = off;
      root.classList.toggle('is-agotado', off);
    }

    // One-line textarea that grows with the text (never shrinks below one line).
    function autosize() {
      var ta = els.message;
      if (!ta) return;
      ta.style.height = 'auto';
      ta.style.height = ta.scrollHeight + 'px';
    }

    function renderCount() {
      if (!els.message) return;
      autosize();
      if (!els.count) return;
      var max = parseInt(els.message.getAttribute('maxlength'), 10) || 200;
      var n = els.message.value.length;
      if (n > max) { els.message.value = els.message.value.slice(0, max); n = max; }
      els.count.textContent = fmt(n >= max ? S.countMax : S.countText, { n: n, max: max });
      els.count.classList.toggle('magno-count--warn', n >= max - 20 && n < max);
      els.count.classList.toggle('magno-count--max', n >= max);
      renderNoMessage();
    }

    // "Sin mensaje" toggle: fills the field with the phrase (so the order carries it); tapping again clears it.
    function isNoMessage() { return !!(S.noMessage && els.message && els.message.value === S.noMessage); }
    function toggleNoMessage() {
      if (!els.message) return;
      els.message.value = isNoMessage() ? '' : (S.noMessage || '');
    }
    function renderNoMessage() {
      var btn = root.querySelector('[data-no-message]');
      if (!btn) return;
      var on = isNoMessage();
      btn.classList.toggle('is-active', on);
      btn.setAttribute('aria-pressed', on ? 'true' : 'false');
    }

    function validate() {
      renderCount();
      var btns = buttons();
      if (state.status === 'agotado') {
        btns.forEach(function (b) { if (!b.disabled) { b.disabled = true; b.setAttribute('data-magno-disabled', ''); } });
      } else {
        btns.forEach(function (b) { if (b.hasAttribute('data-magno-disabled')) { b.disabled = false; b.removeAttribute('data-magno-disabled'); } });
      }
      var missing = missingFields();
      if (missing.indexOf('date') === -1) { if (els.dateInput) els.dateInput.classList.remove('is-invalid'); setError(els.dateError, ''); }
      if (missing.indexOf('message') === -1) { if (els.message) els.message.classList.remove('is-invalid'); setError(els.messageError, ''); }
      var hint = ctaHint();
      if (hint) {
        var nac = isCourier();
        var text = state.status === 'agotado' ? '' :
          (missing.length === 2 ? (nac ? S.hintBothNacional : S.hintBoth) : missing[0] === 'date' ? (nac ? S.hintDateNacional : S.hintDate) : missing[0] === 'message' ? S.hintMessage : '');
        hint.textContent = text;
        hint.hidden = !text;
      }
      renderSticky();
    }

    /* ---- mobile sticky add-to-cart bar ---- */

    var sticky = null;
    function ensureSticky() {
      if (sticky || !document.body || !('IntersectionObserver' in window)) return;
      var real = addButton();
      if (!real) return;
      sticky = document.createElement('div');
      sticky.className = 'magno-sticky';
      sticky.hidden = true;
      sticky.innerHTML = '<div class="magno-sticky__info"><span class="magno-sticky__price"></span><span class="magno-sticky__status"></span></div>' +
        '<button type="button" class="btn--full btn--primary magno-sticky__btn"></button>';
      document.body.appendChild(sticky);
      sticky.querySelector('.magno-sticky__btn').addEventListener('click', function () {
        if (state.status === 'agotado') return;
        var missing = missingFields();
        if (missing.length) { showMissing(missing); return; }
        real.click();
      });
      var io = new IntersectionObserver(function (entries) {
        sticky.setAttribute('data-away', entries[0].isIntersecting ? 'false' : 'true');
        renderSticky();
      }, { threshold: 0 });
      io.observe(real);
    }

    function renderSticky() {
      if (!sticky) return;
      var real = addButton();
      var away = sticky.getAttribute('data-away') === 'true';
      var show = away && state.status !== 'agotado' && real && !real.disabled;
      sticky.hidden = !show;
      document.body.classList.toggle('magno-sticky-visible', show);
      if (!show) return;
      var priceEl = real.querySelector('[data-button-price]');
      sticky.querySelector('.magno-sticky__price').textContent = priceEl ? priceEl.textContent.trim() : '';
      sticky.querySelector('.magno-sticky__btn').textContent = missingFields().length ? (isCourier() ? (S.stickyChooseNacional || S.stickyChoose) : S.stickyChoose) : S.stickyAdd;
      var st = sticky.querySelector('.magno-sticky__status');
      var html = statusHtml();
      if (st.innerHTML !== html) st.innerHTML = html;
    }

    function refreshPicker() {
      if (!fp) return;
      var today = fromYmd(state.now.ymd);
      fp.set('minDate', today);
      fp.set('maxDate', addDays(today, maxDays));
      fp.set('disable', [function (d) { return !isDeliverable(ymd(d)); }]);
      var sel = fp.selectedDates[0];
      if (sel && !isDeliverable(ymd(sel))) fp.clear();
      fp.redraw();
      renderChips();
    }

    function renderAll() {
      computeState();
      renderAgotado();
      renderMessages();
      renderLabels();
      refreshPicker();
      renderArrival();
      renderStatus();
      validate();
      state.lastKey = state.now.ymd + '|' + state.pastCutoff + '|' + (state.city && state.city.handle) + '|' + state.variantId;
    }

    // Every second: update the countdown; re-render fully only when the day or cutoff flips.
    function tick() {
      if (document.hidden) return;
      var prev = state.lastKey;
      computeState();
      var key = state.now.ymd + '|' + state.pastCutoff + '|' + (state.city && state.city.handle) + '|' + state.variantId;
      if (key !== prev) renderAll();
      else renderStatus();
    }

    /* ---- wiring ---- */

    whenFlatpickrReady(function () {
      computeState();
      var today = fromYmd(state.now.ymd);
      if (els.dateInput) {
        fp = window.flatpickr(els.dateInput, {
          minDate: today,
          maxDate: addDays(today, maxDays),
          disable: [function (d) { return !isDeliverable(ymd(d)); }],
          dateFormat: 'd/m/y',
          disableMobile: true,
          locale: (locale === 'es' && window.flatpickr.l10ns && window.flatpickr.l10ns.es) ? 'es' : 'default',
          onChange: function () { renderArrival(); renderChips(); validate(); }
        });
      }
      if (els.chips) {
        els.chips.addEventListener('click', function (e) {
          var b = e.target.closest('[data-chip]');
          if (!b || !fp) return;
          var v = b.getAttribute('data-chip');
          if (v === 'other') { fp.open(); return; }
          fp.setDate(fromYmd(v), true);
        });
      }
      // Capture-phase guards: run before the theme's own add-to-cart / Shop Pay handlers.
      var scope = formScope();
      scope.addEventListener('click', function (e) {
        var t = e.target;
        if (!t || !t.closest) return;
        if (t.closest('[data-add-to-cart]') || t.closest('.shopify-payment-button')) { guardSubmit(e); return; }
        // "Sin mensaje": handled here in the capture phase and stopped, because the theme's form
        // script otherwise treats a click on any button inside the form as an add-to-cart.
        if (t.closest('[data-no-message]') && els.message) {
          e.preventDefault();
          e.stopPropagation();
          toggleNoMessage();
          validate();
        }
      }, true);
      if (els.form) els.form.addEventListener('submit', guardSubmit, true);
      ensureSticky();
      renderAll();
      setInterval(tick, 1000);
      document.addEventListener('visibilitychange', function () { if (!document.hidden) renderAll(); });
      document.addEventListener(CITY_EVENT, renderAll);
      if (els.message) {
        els.message.addEventListener('input', validate);
        // Tapping into a "Sin mensaje" field selects the phrase so typing replaces it.
        els.message.addEventListener('focus', function () {
          if (isNoMessage()) { try { els.message.select(); } catch (e) { /* ignore */ } }
        });
      }
      if (els.dateInput) els.dateInput.addEventListener('change', validate);
      // Payment buttons render late; re-check once they exist.
      setTimeout(validate, 1500);
    });

    window.MagnoDelivery = { state: state, cities: cities, isDeliverable: isDeliverable, render: renderAll, missingFields: missingFields, chipCandidates: chipCandidates };
  }

  /* ------------------------------------------------------------------ */

  function boot() {
    initSelector();
    initAnnouncement();
    initFaq();
    Array.prototype.slice.call(document.querySelectorAll('[data-magno-delivery]')).forEach(initCalendar);
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();
})();